import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Chrome, Eye, Ban, Clock, Download, ChevronRight, Check } from 'lucide-react';

export default function MonitorPage() {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">AgentCheck</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm">
              Site Scan
            </Link>
            <span className="text-white font-medium text-sm">
              Monitor (Browser)
            </span>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2 mb-6">
            <Chrome className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">Chrome Extension</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AgentCheck Monitor
          </h1>
          <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
            See what contacts you while browsing. Continuous third-party tracking visibility with Shut/Pause/Continue controls.
          </p>

          {/* Install CTA */}
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-xl mx-auto">
            <h2 className="text-lg font-semibold text-white mb-2">Install Chrome Monitor</h2>
            <p className="text-slate-400 text-sm mb-6">
              Currently available for manual installation. Chrome Web Store listing coming soon.
            </p>
            
            <div className="space-y-3 text-left mb-6">
              <Step number={1} text='Go to chrome://extensions/' />
              <Step number={2} text="Enable Developer Mode (top right toggle)" />
              <Step number={3} text='Click "Load unpacked"' />
              <Step number={4} text="Select the extension dist folder" />
            </div>

            <a
              href="/extension/agentcheck-monitor-v0.1.0.zip"
              download
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Extension ZIP
            </a>
            <p className="text-slate-500 text-xs mt-3">
              v0.1.0 • 118 KB • Requires Chrome 120+
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-12">
            What You Get
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Eye className="w-6 h-6" />}
              title="Real-Time Monitoring"
              description="See every third-party request as you browse. Know who's tracking you across websites."
            />
            <FeatureCard
              icon={<Ban className="w-6 h-6" />}
              title="Shut Companies"
              description="Block all requests to any tracking company with one click. Permanent until you continue."
            />
            <FeatureCard
              icon={<Clock className="w-6 h-6" />}
              title="Pause Trackers"
              description="Temporarily block trackers for 2 hours, 24 hours, or 7 days. Auto-expires."
            />
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Privacy First
          </h2>
          
          <div className="bg-slate-800/50 rounded-xl p-6 space-y-4">
            <PrivacyItem text="All data stored locally on your device" />
            <PrivacyItem text="No browsing history sent to any server" />
            <PrivacyItem text="Minimal permissions at install (storage, alarms only)" />
            <PrivacyItem text="Monitor & blocking permissions requested at runtime" />
            <PrivacyItem text="'This site only' mode available for selective monitoring" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Already have tracking data?
          </h2>
          <p className="text-slate-400 mb-6">
            Use our free Site Scan to analyze any website's trackers instantly.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
          >
            Try Site Scan
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          AgentCheck • See what contacts you
        </div>
      </footer>
    </div>
  );
}

function Step({ number, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0">
        {number}
      </div>
      <span className="text-slate-300 text-sm">{text}</span>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
      <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm">{description}</p>
    </div>
  );
}

function PrivacyItem({ text }) {
  return (
    <div className="flex items-center gap-3">
      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
      <span className="text-slate-300">{text}</span>
    </div>
  );
}
